import {
  ComboboxItem,
  ComboboxLikeRenderOptionInput,
  Group,
  SelectProps,
  Stack,
  Text,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { CountryLabel, LabelWithTooltip, Select } from "@tfg/ui"
import { sortBy } from "lodash"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { SupplierResponses } from "@tfg/types/endpoints/supplier"

import { IconManufacturing } from "~/components/icons/activities/IconManufacturing"
import { useListSupplierProductionSitesQuery } from "~/pages/SupplierPortal/ProductionSites/hooks/useListProductionSiteQuery.ts"
import { ProductionSiteDrawer } from "~/pages/SupplierPortal/TraceabilityRequests/components/ProductionSiteDrawer.tsx"
import { useAuth } from "~/store/auth-context.tsx"
import { COUNTRIES } from "~/utils/countries.ts"

interface Props extends Partial<SelectProps> {
  productionSiteId: string | null
  onProductionSiteSelected: (productionSiteId: string | null) => void
  disabled?: boolean
  isRequired?: boolean
  tooltip?: string
}

export const ProductionSiteSelect = ({
  productionSiteId,
  onProductionSiteSelected,
  disabled,
  isRequired,
  tooltip,
}: Props) => {
  const { t } = useTranslation(["traceability"])

  const [modalOpen, modalActions] = useDisclosure(false)

  const { data: productionSites } = useListSupplierProductionSitesQuery()
  const { isAdmin } = useAuth()
  const [searchValue, setSearchValue] = useState("")
  const selectOptions = useMemo(() => {
    const sortedSites = sortBy(productionSites, (ps) => ps.name)

    const result = sortedSites
      .filter((ps) => !ps.deletedAt || ps.id === productionSiteId)
      .map((productionSite) => ({
        value: productionSite.id,
        label: `${productionSite.name} ${productionSite.address ? `(${productionSite.address})` : ""}`,
        productionSite,
      }))
    return result
  }, [productionSites, productionSiteId])

  return (
    <>
      <Select
        data={selectOptions}
        value={productionSiteId}
        searchable
        clearable
        creatable={true}
        error={!productionSiteId && !disabled}
        placeholder={t("productionSiteSelect.placeholders.productionSite")}
        disabled={disabled}
        searchValue={searchValue}
        onSearchChange={(value) => {
          setSearchValue(value)
        }}
        copyableValue={isAdmin ? "productionSiteId" : undefined}
        isAdmin={isAdmin}
        label={
          <LabelWithTooltip
            justify="space-between"
            label={t("productionSiteSelect.labels.productionSite")}
            hoverHelperText={
              tooltip && tooltip.length > 0 ? tooltip : undefined
            }
            withAsterisk={isRequired}
          />
        }
        createLabel={t("productionSiteSelect.labels.addNewProductionSite")}
        onChange={(value) => {
          value
            ? onProductionSiteSelected(value)
            : onProductionSiteSelected(null)
        }}
        onCreate={() => {
          modalActions.open()
          return null
        }}
        leftSection={<IconManufacturing size={16} />}
        comboboxProps={{ width: 400 }}
        customRender={({
          option,
          checked,
        }: ComboboxLikeRenderOptionInput<
          ComboboxItem & { productionSite?: SupplierResponses.ProductionSite }
        >) => {
          const productionSite = option.productionSite
          return (
            <Group
              justify="space-between"
              data-checked={checked ? "true" : undefined}
              p="sm"
              w="100%"
            >
              <Stack gap={0}>
                <Text fw={700}>{productionSite?.name}</Text>
                {productionSite?.country && (
                  <Group gap="xs">
                    <CountryLabel
                      countryIso3166={productionSite.country}
                      width={12}
                      countriesMapper={COUNTRIES}
                    />
                    <Text size="xs" style={{ width: "100%" }}>
                      {productionSite.address}
                    </Text>
                  </Group>
                )}
              </Stack>
            </Group>
          )
        }}
      />
      {modalOpen && (
        <ProductionSiteDrawer
          defaultName=""
          opened={modalOpen}
          onClose={modalActions.close}
          onProductionSiteCreated={(productionSiteIdCreated) => {
            onProductionSiteSelected(productionSiteIdCreated)
          }}
        />
      )}
    </>
  )
}
